// Single source of truth for the optional analytics field columns on the
// Projects table. `createProjectColumns` (which renders them) and
// `ProjectsList` (which configures the shared `TableFieldsDropdown` to
// toggle them) both read this list, so they can never drift out of sync.
// Mirrors the analytics EXPERIENCE_COLUMNS pattern in
// `pages/media/analytics/components/experienceColumns.ts`.

export type ProjectFieldColumnKey =
  | "name"
  | "lastModified"
  | "type"
  | "tags"
  | "status"
  | "views"
  | "videoReproductions"
  | "documentInteractions"
  | "chatConversations";

export type ProjectFieldColumnConfig = {
  key: ProjectFieldColumnKey;
  labelKey: string;
  /** false = pinned column the user can't hide (experience name). */
  removable: boolean;
  /** Shown by default before the user customises anything. */
  defaultVisible: boolean;
};

export const MAX_VISIBLE_PROJECT_FIELD_COLUMNS = 6;

export const PROJECT_FIELD_COLUMNS: ProjectFieldColumnConfig[] = [
  { key: "name", labelKey: "name", removable: false, defaultVisible: true },
  {
    key: "lastModified",
    labelKey: "lastModified",
    removable: true,
    defaultVisible: true,
  },
  { key: "type", labelKey: "type", removable: true, defaultVisible: true },
  { key: "tags", labelKey: "tags", removable: true, defaultVisible: true },
  { key: "status", labelKey: "status", removable: true, defaultVisible: true },
  { key: "views", labelKey: "views", removable: true, defaultVisible: false },
  {
    key: "videoReproductions",
    labelKey: "videoReproductions",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "documentInteractions",
    labelKey: "documentInteractions",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "chatConversations",
    labelKey: "chatConversations",
    removable: true,
    defaultVisible: false,
  },
];

export const DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS: ProjectFieldColumnKey[] =
  PROJECT_FIELD_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const VALID_KEYS = new Set(PROJECT_FIELD_COLUMNS.map((c) => c.key));

export const normalizeVisibleProjectFieldColumns = (
  keys: ProjectFieldColumnKey[]
): ProjectFieldColumnKey[] => {
  const selected = new Set(keys.filter((k) => VALID_KEYS.has(k)));
  const ordered = PROJECT_FIELD_COLUMNS.map((c) => c.key).filter((k) =>
    selected.has(k)
  );
  return ordered.slice(0, MAX_VISIBLE_PROJECT_FIELD_COLUMNS);
};
